import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { handoversApi } from '../lib/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { ArrowRightLeft, AlertTriangle, ExternalLink } from 'lucide-react';

export function HandoverNotificationPopup() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isCoordinator, isChairman } = useAuth();
  
  // State for coordinator pending handovers
  const [pendingHandovers, setPendingHandovers] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [selectedHandover, setSelectedHandover] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  
  // State for chairman escalated handovers
  const [escalatedHandovers, setEscalatedHandovers] = useState([]);
  const [showEscalatedModal, setShowEscalatedModal] = useState(false);

  // Fetch pending/escalated handovers on mount and periodically
  useEffect(() => {
    if (!user) return;
    
    const fetchHandovers = async () => {
      try {
        if (isCoordinator()) {
          const res = await handoversApi.getPendingForMe();
          setPendingHandovers(res.data || []);
          if (res.data && res.data.length > 0 && !showPendingModal) {
            setShowPendingModal(true);
          } else if (!res.data || res.data.length === 0) {
            setShowPendingModal(false);
          }
        }
        
        if (isChairman()) {
          const res = await handoversApi.getEscalated();
          setEscalatedHandovers(res.data || []);
          if (res.data && res.data.length > 0 && !showEscalatedModal) {
            setShowEscalatedModal(true);
          } else if (!res.data || res.data.length === 0) {
            setShowEscalatedModal(false);
          }
        }
      } catch (error) {
        console.error('Failed to fetch handovers:', error);
      }
    };

    fetchHandovers();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchHandovers, 30000);
    return () => clearInterval(interval);
  }, [user, isCoordinator, isChairman, location.pathname]);

  // Handle accept handover
  const handleAccept = async (handover) => {
    setLoading(true);
    try {
      await handoversApi.accept(handover.id);
      toast.success('Handover accepted. You are now the coordinator.');
      const newList = pendingHandovers.filter(h => h.id !== handover.id);
      setPendingHandovers(newList);
      if (newList.length === 0) {
        setShowPendingModal(false);
      }
      // Navigate to the request
      navigate(`/requests/${handover.request_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept handover');
    } finally {
      setLoading(false);
    }
  };

  // Handle reject handover
  const handleReject = async () => {
    if (!selectedHandover || !rejectReason?.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setLoading(true);
    try {
      await handoversApi.reject(selectedHandover.id, rejectReason);
      toast.success('Handover rejected. Escalated to Chairman.');
      const newList = pendingHandovers.filter(h => h.id !== selectedHandover.id);
      setPendingHandovers(newList);
      setShowRejectModal(false);
      setSelectedHandover(null);
      setRejectReason('');
      if (newList.length === 0) {
        setShowPendingModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject handover');
    } finally {
      setLoading(false);
    }
  };

  // Handle view request (for coordinator)
  const handleViewRequest = (handover) => {
    setShowPendingModal(false);
    navigate(`/requests/${handover.request_id}`);
  };

  // Handle chairman view request
  const handleChairmanView = async (handover) => {
    // Acknowledge and navigate
    setLoading(true);
    try {
      await handoversApi.acknowledge(handover.id);
      const newList = escalatedHandovers.filter(h => h.id !== handover.id);
      setEscalatedHandovers(newList);
      if (newList.length === 0) {
        setShowEscalatedModal(false);
      }
      navigate(`/requests/${handover.request_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setLoading(false);
    }
  };

  // Handle chairman reassign (navigate to request to reassign)
  const handleChairmanReassign = async (handover) => {
    setLoading(true);
    try {
      await handoversApi.acknowledge(handover.id);
      const newList = escalatedHandovers.filter(h => h.id !== handover.id);
      setEscalatedHandovers(newList);
      if (newList.length === 0) {
        setShowEscalatedModal(false);
      }
      // Navigate to request page to reassign
      navigate(`/requests/${handover.request_id}`);
      toast.info('Please use the Handover button to reassign the coordinator.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Coordinator: Pending Handovers Modal */}
      <Dialog open={showPendingModal} onOpenChange={setShowPendingModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ArrowRightLeft className="w-5 h-5" />
              Handover Request{pendingHandovers.length > 1 ? 's' : ''} Pending
            </DialogTitle>
            <DialogDescription>
              {pendingHandovers.length === 1 
                ? 'A coordinator has requested to hand over a request to you.'
                : `${pendingHandovers.length} coordinators have requested to hand over requests to you.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {pendingHandovers.map((handover) => (
              <div 
                key={handover.id} 
                className="border border-amber-200 bg-amber-50 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">
                      {handover.request_ref_no}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {handover.request_title}
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      <strong>From:</strong> {handover.from_coordinator_name}
                    </p>
                    <p className="text-sm text-slate-500">
                      <strong>Reason:</strong> {handover.reason}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button 
                    onClick={() => handleAccept(handover)}
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    size="sm"
                    data-testid={`accept-handover-${handover.id}`}
                  >
                    Accept
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => { setSelectedHandover(handover); setShowRejectModal(true); }}
                    disabled={loading}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    size="sm"
                    data-testid={`reject-handover-${handover.id}`}
                  >
                    Reject
                  </Button>
                  <Button 
                    variant="ghost"
                    onClick={() => handleViewRequest(handover)}
                    size="sm"
                    className="ml-auto"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View Request
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Handover Reason Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Handover</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this handover. This will be escalated to the Chairman.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason for Rejection <span className="text-red-500">*</span></Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why you cannot accept this handover..."
                rows={3}
                data-testid="global-reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleReject}
              disabled={loading || !rejectReason?.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-global-reject-btn"
            >
              {loading ? 'Processing...' : 'Reject & Escalate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chairman: Escalated Handovers Modal */}
      <Dialog open={showEscalatedModal} onOpenChange={setShowEscalatedModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Handover Rejection{escalatedHandovers.length > 1 ? 's' : ''} Escalated
            </DialogTitle>
            <DialogDescription>
              {escalatedHandovers.length === 1 
                ? 'A handover request has been rejected and requires your attention.'
                : `${escalatedHandovers.length} handover requests have been rejected and require your attention.`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {escalatedHandovers.map((handover) => (
              <div 
                key={handover.id} 
                className="border border-red-200 bg-red-50 rounded-lg p-4"
              >
                <div className="flex-1">
                  <p className="font-medium text-slate-900">
                    {handover.request_ref_no}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {handover.request_title}
                  </p>
                  <div className="mt-2 space-y-1 text-sm">
                    <p className="text-slate-500">
                      <strong>Original Coordinator:</strong> {handover.from_coordinator_name}
                    </p>
                    <p className="text-slate-500">
                      <strong>Rejected by:</strong> {handover.to_coordinator_name}
                    </p>
                    <p className="text-red-600">
                      <strong>Rejection Reason:</strong> {handover.rejection_reason}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button 
                    onClick={() => handleChairmanReassign(handover)}
                    disabled={loading}
                    className="bg-blue-900 hover:bg-blue-950"
                    size="sm"
                    data-testid={`reassign-${handover.id}`}
                  >
                    <ArrowRightLeft className="w-4 h-4 mr-1" />
                    Reassign
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => handleChairmanView(handover)}
                    disabled={loading}
                    size="sm"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
